import React, { useState } from "react";
import styled from "@emotion/styled";
import { DisabledOverlay, DragDropFileUpload, SimpleTooltip, StyledCustomDialog } from "@iventis/components";
import { DialogTitle } from "@mui/material";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Content } from "@iventis/translations";
import { getImageDimensions, megaBytesToBytes, resizeFileImage, resizeImageGivenMaxWidthAndHeight, fileToBase64 } from "@iventis/utilities";
import { toast } from "@iventis/toasts/src/toast";
import { media } from "@iventis/styles";
import { CommentImage } from "./comment-types";

/** Max file size we accept */
const MAX_FILE_SIZE_MB = 15;

/** Size of image has to be after resize */
const MAX_FILE_SIZE_AFTER_RESIZE_MB = 3.75;

/**
 * Size of image if we are going to resize it.
 * This is 2.8 as base64 seems to usually increase by about 1/3, and 2.8 + 1/3 (0.86) puts us under the max 3.75
 */
const FILE_SIZE_TO_RESIZE_MB = 2.8;

/** Max width of image */
const MAX_WIDTH_PX = 1920;

/** Max height of image */
const MAX_HEIGHT_PX = 1080;

export const CommentImageUpload = ({
    onFileUpload,
    disableImageIcon,
    replaceImageText,
}: {
    onFileUpload: (image: CommentImage) => void;
    disableImageIcon: boolean;
    replaceImageText: boolean;
}) => {
    const translate = useIventisTranslate();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const inputRef = React.createRef<HTMLInputElement>();

    const showNotificationForFileTooLarge = () => {
        toast.warning({
            title: translate(Content.common.warning),
            message: translate(Content.map7.importGeoJson.fileTooLarge.message, { size: `${MAX_FILE_SIZE_MB} Mb` }),
        });
    };

    const handleFileUpload = async (file: File) => {
        // Check if file is a valid image
        if (file.type !== "image/jpeg" && file.type !== "image/png" && file.type !== "image/jpg") {
            toast.warning({
                title: translate(Content.common.warning),
                message: translate(Content.map8.fileUpload.invalid),
            });
            return;
        }

        let compressedFile = file;
        let { height, width } = await getImageDimensions(file);

        // If file size is too big to compress
        if (file.size > megaBytesToBytes(MAX_FILE_SIZE_MB)) {
            showNotificationForFileTooLarge();
            return;
        }

        // If file size is too big and needs resizing
        if (file.size > megaBytesToBytes(FILE_SIZE_TO_RESIZE_MB)) {
            const { height: newHeight, width: newWidth } = resizeImageGivenMaxWidthAndHeight(width, height, MAX_WIDTH_PX, MAX_HEIGHT_PX);
            height = newHeight;
            width = newWidth;
            compressedFile = await resizeFileImage(file, newWidth, newHeight);
            // If file is still too big after resizing
        }

        if (compressedFile.size > megaBytesToBytes(MAX_FILE_SIZE_AFTER_RESIZE_MB)) {
            showNotificationForFileTooLarge();
            return;
        }

        const base64 = await fileToBase64(compressedFile);

        // Check the size of the base64 string
        const textEncoder = new TextEncoder();
        if (textEncoder.encode(base64.withHeader).length > megaBytesToBytes(MAX_FILE_SIZE_AFTER_RESIZE_MB)) {
            showNotificationForFileTooLarge();
            return;
        }

        onFileUpload({ base64Image: base64.withHeader, imageName: file.name, height, width });
        setIsModalOpen(false);
    };

    return (
        <>
            {isModalOpen && (
                <>
                    <StyledCustomDialog $overflow="hidden" maxWidth={false} open onClose={() => setIsModalOpen(false)}>
                        <StyledDialogTitle>
                            <StyledCommentIcon icon={["fas", "file-arrow-up"]} /> {translate(Content.map8.fileUpload.image_comment)}
                        </StyledDialogTitle>
                        <DragDropFileUpload supportedExtensions={["png", "jpeg", "jpg"]} onFileUpload={handleFileUpload} maxFileSize={MAX_FILE_SIZE_MB} />
                    </StyledCustomDialog>
                </>
            )}
            <SimpleTooltip
                disabled={disableImageIcon}
                text={replaceImageText ? translate(Content.map8.comments.replaceImage) : translate(Content.map8.comments.image)}
                placement="bottom"
            >
                <StyledButtonContainer>
                    {disableImageIcon && <DisabledOverlay />}
                    <StyledButton onClick={() => setIsModalOpen(true)} data-testid="image-upload-button">
                        <StyledCommentIcon icon={["far", "camera"]} size="lg" />
                    </StyledButton>
                    <StyledMobileButton onClick={() => inputRef.current.click()} data-testid="image-upload-button-mobile">
                        <StyledCommentIcon icon={["far", "camera"]} size="lg" />
                        <StyledInput ref={inputRef} type="file" data-testid="comment-image-upload-file-mobile" onChange={(event) => handleFileUpload(event.target.files[0])} />
                    </StyledMobileButton>
                </StyledButtonContainer>
            </SimpleTooltip>
        </>
    );
};

const StyledButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 14px;
    display: none;
    ${media.medium} {
        display: block;
    }
`;

const StyledInput = styled.input`
    display: none;
`;

const StyledMobileButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 14px;
    ${media.medium} {
        display: none;
    }
`;

const StyledDialogTitle = styled(DialogTitle)`
    display: flex;
    gap: 8px;
    place-items: center;
`;

export const StyledCommentIcon = styled(FontAwesomeIcon)`
    color: ${({ theme }) => theme.typographyColors.core};
`;

const StyledButtonContainer = styled.span`
    position: relative;
`;
